import {
  ActionIcon,
  Flex,
  FocusTrap,
  Space,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  useDebouncedCallback,
  useDisclosure,
} from '@mantine/hooks';
import {
  openModal,
} from '@mantine/modals';
import AddIcon from '@mui/icons-material/Add';
import {
  useForm,
} from '@tanstack/react-form';
import {
  useQueryClient,
} from '@tanstack/react-query';
import {
  createFileRoute, Link,
} from '@tanstack/react-router';
import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useAuth,
} from 'react-oidc-context';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  ClientsApiResponse,
  ClientWithId,
  parseClientsApiResponse,
  type ClientParsed,
} from '#features/client/Client.schema';
import {
  CompanyApiResponse,
} from '#features/digital-workers/Company.schema';
import AddUserModal from '#features/scout/DigitalAssistants/Settings/AddUserModal';
import {
  AtoSetup,
} from '#features/scout/DigitalAssistants/SetUp/Reporter/AtoSetup';
import ConfirmDeleteModal from '#features/scout/DigitalAssistants/SetUp/Reporter/ConfirmDeleteModal';
import CustomerTable from '#features/scout/DigitalAssistants/SetUp/Reporter/CustomerTable';
import {
  UserDetailModal,
} from '#features/settings/UserDetailModal';
import UploadBulk from '#features/settings/UploadBulk';
import SearchInput from '#src/components/ui/input/SearchInput';
// import {
//   type RootState,
// } from '#src/state/store';
import {
  resolveObject,
} from '@specia-accountant/shared';

interface ClientsLoaderData {
  company: CompanyApiResponse;
  clients: ClientsApiResponse;
}
export const Route = createFileRoute(
  '/_app/_authenticated/mydigitalworkers/scout/_scoutLayout/clients',
)({
  beforeLoad: () => {
    return {
      crumb: 'Clients',
    };
  },
  component: RouteComponent,
  loader: async (opts) => {
    const company = opts.context.queryClient.ensureQueryData({
      queryKey: ['company'],
    }) satisfies Promise<CompanyApiResponse>;
    const clients = opts.context.queryClient.ensureQueryData({
      queryKey: [
        'reporter',
        'admin',
        'clients',
      ],
    }) satisfies Promise<ClientWithId[]>;
    const users = opts.context.queryClient.ensureQueryData({
      queryKey: [
        'reporter',
        'users',
      ],
    });
    const connectedUser = opts.context.queryClient.ensureQueryData({
      queryKey: [
        'reporter',
        'users',
      ],
    });
    return resolveObject({
      company,
      clients: parseClientsApiResponse(clients),
      users,
    });
  },
});

function RouteComponent(_props) {
  const {
    company,
    clients,
    users,
  } = Route.useLoaderData();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const [
    uploadOpened,
    uploadHandlers,
  ] = useDisclosure(false);
  const [
    deleteOpened,
    deleteHandlers,
  ] = useDisclosure(false);
  const [
    detailOpened,
    detailHandlers,
  ] = useDisclosure(false);
  const [
    searchOpened,
    searchHandlers,
  ] = useDisclosure(false);
  const [
    addOpened,
    addHandlers,
  ] = useDisclosure(false);
  const [
    errorOpened,
    errorHandlers,
  ] = useDisclosure(false);
  const [
    assignmentOpened,
    assignmentHandlers,
  ] = useDisclosure(false);
  const [
    tier,
    setTier,
  ] = useState<'paid' | 'free'>('paid');
  const [
    customerData,
    setCustomerData,
  ] = useState<ClientParsed[]>([]);
  const [
    currentPage,
    setCurrentPage,
  ] = useState(1);
  const [
    deleteID,
    setDeleteID,
  ] = useState<string>();
  const [
    currentABN,
    setCurrentABN,
  ] = useState<string>();
  const [
    selectedClient,
    setSelectedClient,
  ] = useState<string>();
  const [
    displayedCustomers,
    setDisplayedCustomers,
  ] = useState<ClientParsed[]>(clients.data);
  const [
    currentAssignmentView,
    setCurrentAssignmentView,
  ] = useState<string[]>([]);
  const [
    error,
    setError,
  ] = useState({
    message: '',
    description: '',
  });
  const [
    searchKey,
    setSearchKey,
  ] = useState('');
  const entryValues = useMemo(() => {
    return clients.data.map(entry => [...Object.values(entry ?? {}).filter(item => typeof item === 'string')].filter(entry => entry ? entry : false),
    ) as string[][];
  }, [clients]);
  const handleSearch = useDebouncedCallback(async (formData) => {
    console.debug('Handle search form data', formData);
    const searchTerm = formData.searchTerm;
    const filterResult = clients.data.filter((_entry: ClientParsed, entryIndex: keyof (typeof entryValues)) => {
      const entryProperties = entryValues[entryIndex] as string[];
      console.debug(entryProperties);
      return entryProperties.some((val) => {
        return (
          typeof val === 'string'
          && val
            .toLowerCase()
            .includes(
              searchTerm.toLowerCase(),
            )
        );
      });
    });
    console.debug(
      `Search term ${searchTerm}, matches ${filterResult.length} / ${clients.data.length}`,
      clients.data,
    );
    setDisplayedCustomers(filterResult);
  }, 250);
  const {
    Field, handleSubmit, state,
  } = useForm({
    defaultValues: {
      searchTerm: '',
    },
    onSubmit: async ({
      value,
    }) => {
      handleSearch(value);
    },
  });

  useEffect(() => {
    if (company.subscription_type === 'hired') {
      setTier('paid');
    } else if (company.subscription_type === 'free') {
      setTier('free');
    }
  }, [company]);
  function changeABNHandler(value, id) {
    setCustomerData((oldState) => {
      let newData = JSON.parse(JSON.stringify(oldState));
      newData.find(item => item.id === id).ABN = value;
      return newData;
    });
  }
  async function addBulkUser(clients) {
    let clientListLength = clients.length;
    console.log(clientListLength);
    let count = 0;
    let subClientList;
    let response;
    while (count + 99 < clientListLength - 1) {
      console.log(`from ${count} to ${count + 99}`);
      subClientList = clients.slice(count, count + 99);
      response = await fetch(`${window.origin}/api/reporter/clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.user?.access_token}`,
        },
        body: JSON.stringify({
          clients: subClientList,
        }),
      });
      if (response.status === 403) {
        auth.signinRedirect();
      }
      count += 100;
    }
    console.log(`from ${count} to ${clientListLength - 1}`);
    subClientList = clients.slice(count, clientListLength - 1);

    response = await fetch(`${window.origin}/api/reporter/clients`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.user?.access_token}`,
      },
      body: JSON.stringify({
        clients: subClientList,
      }),
    });
    if (response.status === 403) {
      auth.signinRedirect();
    }
  }
  async function addUser(client) {
    let response = await fetch(`${window.origin}/api/reporter/clients`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.user?.access_token}`,
      },
      body: JSON.stringify({
        clients: [client],
        tier: 'paid',
      }),
    });
    if (response.status === 403) {
      auth.signinRedirect();
    }
  }
  function deleteCustomer() {
    deleteHandlers.close();
    detailHandlers.close();
    let id = deleteID;
    setCustomerData(oldState => oldState.filter(item => item.id !== id));
    setDisplayedCustomers(oldState => oldState.filter(item => item.id !== id));
    fetch(`${window.origin}/api/reporter/clients?id=${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.user?.access_token}`,
      },
    }).then((response) => {
      if (response.status === 403) {
        auth.signinRedirect();
      } else {
        return response.json();
      }
    });
  }
  function changeadminAccountantEmailsHandler(value, id) {
    setCustomerData((oldState) => {
      let newData = JSON.parse(JSON.stringify(oldState));
      newData.find(item => item.id === id).adminAccountantEmails = value;
      return newData;
    });
  }
  function changeClientNameHandler(
    value: string,
    id: string,
  ) {
    setCustomerData((oldState) => {
      let newData: ClientParsed[] = JSON.parse(JSON.stringify(oldState));
      const clientIndex = newData.findIndex(item => item.id === id);
      if (clientIndex) {
        newData[clientIndex].clientname = value;
      }
      return newData;
    });
  }
  function openAssignmentModal(
    abn: string,
    _currentCustomerId?: string,
  ) {
    setCurrentABN(abn);
    // serCurrentCustomerId(_currentCustomerId)
    const currentAssignment: ClientParsed | undefined = customerData.find(item => item.clientabn === abn);
    if (currentAssignment) {
      setCurrentAssignmentView(currentAssignment?.agentemails);
      assignmentHandlers.open();
    }
  }

  return (
    <>
      <Flex
        w="100%"
        direction="column"
      >
        <Flex
          id="requests-table-search"
          direction="row"
          align="center"
          justify="space-between"
        >
          <form
            id="client-search-bar"
            onSubmit={ (e) => {
              e.preventDefault();
            // handleSubmit()a
            } }
            onChange={ (e) => {
              e.preventDefault();
              handleSubmit();
            } }
          >
            <Field
              name="searchTerm"
              children={ ({
                state,
                handleChange,
                handleBlur,
              }) => (
                <SearchInput
                  data={ [] }
                  onChange={ (evt: string): void => handleChange(evt) }
                  onBlur={ handleBlur }
                  value={ state.value }
                />
              ) }
            />
          </form>
          <Space
            w="lg"
          />
          <Tooltip
            id="tooltip-submit"
            label="Create Request"
            position="left"
            arrowPosition="side"
            arrowOffset={ 2 }
            arrowSize={ 12 }
            withArrow
          >
            <ActionIcon
              id="add-client"
              variant="gradient"
              size="xl"
              radius="xl"
              aria-label="Create workpaper"
              gradient={ {
                from: 'oklch(1 0 0 / 0.1)',
                to: 'oklch(1 0 0 / 0.2)',
                deg: 30,
              } }
              onClick={ () => {
                openModal({
                  closeOnClickOutside: true,
                  withCloseButton: true,
                  withinPortal: true,
                  title: (
                    <Text
                      c="light.2"
                      size="xl"
                    >
                      Add Client
                    </Text>
                  ),
                  children: (
                    <>
                      <FocusTrap.InitialFocus />
                    </>
                  ),
                });
              } }
            >
              <AddIcon />
            </ActionIcon>
          </Tooltip>
        </Flex>
        <Space
          h="xl"
        />
        <CustomerTable
          clients={ clients.data }
          onChangedABN={ changeABNHandler }
          onChangedClientName={ changeClientNameHandler }
          onChangedadminAccountantEmails={ changeadminAccountantEmailsHandler }
          customerData={ displayedCustomers }
          openAssignmentModal={ openAssignmentModal }
          openDetailModalHandler={ detailHandlers.open }
          setSelectedClientHandler={ setSelectedClient }
          currentPage={ currentPage }
          previousPageHandler={ () => { setCurrentPage(oldState => oldState - 1); } }
          nextPageHandler={ () => { setCurrentPage(oldState => oldState + 1); } }
          isLoading={ false }
        />

        <UploadBulk
          uploadModalShow={ uploadOpened }
          closeUploadModal={ uploadHandlers.close }
        />
        <ConfirmDeleteModal
          show={ deleteOpened }
          onClose={ deleteHandlers.close }
          onDelete={ deleteCustomer }
        />
      </Flex>
    </>
  );
};
